import { Alert, Badge, Button, Card, Group, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ListingCard } from '../components/ListingCard.js';
import { getFavorites, REJECT_CATEGORIES, updateStatus, type ObjectView } from '../lib/api.js';

const CAT_LABEL: Record<string, string> = Object.fromEntries(
  REJECT_CATEGORIES.map((c) => [c.value, c.label]),
);

/** Neueste Ablehnung aus dem Event-Verlauf — quellen-bewusst (Partner vs. selbst), Grund + Notiz. */
function rejectInfo(
  obj: ObjectView,
): { by: 'partner' | 'user'; label: string; note: string | null } | null {
  const ev = (obj.events ?? []).find((e) => e.eventType === 'rejected');
  if (!ev || !ev.payload) return null;
  const cat = typeof ev.payload.category === 'string' ? ev.payload.category : '';
  const note = typeof ev.payload.note === 'string' && ev.payload.note ? ev.payload.note : null;
  return {
    by: ev.source === 'partner' ? 'partner' : 'user',
    label: CAT_LABEL[cat] ?? cat ?? 'abgelehnt',
    note,
  };
}

type Filter = 'alle' | 'offen' | 'abgelehnt';

export function Merkliste(): JSX.Element {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('alle');
  const { data, isLoading } = useQuery({ queryKey: ['favorites'], queryFn: getFavorites });

  const reactivate = useMutation({
    mutationFn: (id: number) => updateStatus(id, 'new'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['favorites'] });
      void qc.invalidateQueries({ queryKey: ['swipe-queue'] });
      void qc.invalidateQueries({ queryKey: ['objects'] });
    },
  });

  if (isLoading) return <Text c="dimmed">Lädt …</Text>;

  // Kein eigener Login (Impersonation / alte Session) → Merken nicht möglich.
  if (data && !data.personalLogin) {
    return (
      <Stack gap="md">
        <Title order={3}>❤️ Merkliste</Title>
        <Alert color="violet" variant="light" title="Eigener Login nötig">
          Deine Merkliste ist privat — nur du siehst sie. Melde dich dafür mit deinem eigenen
          Google-Login an (nicht als angesehenes Konto), dann kannst du Objekte per ♥ merken. Was du
          merkst, bleibt hier — auch wenn dein Partner es ablehnt.
        </Alert>
      </Stack>
    );
  }

  const all = data?.objects ?? [];
  const rejectedCount = all.filter((o) => o.status === 'rejected').length;
  const shown = all.filter((o) =>
    filter === 'alle'
      ? true
      : filter === 'offen'
        ? o.status !== 'rejected'
        : o.status === 'rejected',
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap" gap="xs">
        <Title order={3}>❤️ Merkliste</Title>
        <Text size="xs" c="dimmed">
          🔒 Nur du siehst deine Merkliste
        </Text>
      </Group>

      {all.length > 0 && (
        <SegmentedControl
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          data={[
            { label: `Alle (${all.length})`, value: 'alle' },
            { label: 'Offen', value: 'offen' },
            { label: `Abgelehnt${rejectedCount > 0 ? ` (${rejectedCount})` : ''}`, value: 'abgelehnt' },
          ]}
          size="xs"
        />
      )}

      {shown.length === 0 && (
        <Card>
          <Stack align="center" gap="sm" py="lg">
            <Text style={{ fontSize: '2rem' }}>🤍</Text>
            <Text fw={600}>{all.length === 0 ? 'Noch nichts gemerkt' : 'Nichts in diesem Filter'}</Text>
            <Text c="dimmed" size="sm" ta="center" maw={320}>
              Tippe auf das ♥ bei einem Objekt (Swipe, Liste oder Detail) — es landet hier und bleibt,
              selbst wenn dein Partner es ablehnt.
            </Text>
            <Button component={Link} to="/" variant="light" mt="sm">
              Zum Swipe →
            </Button>
          </Stack>
        </Card>
      )}

      {shown.map((o) => {
        const ri = o.status === 'rejected' ? rejectInfo(o) : null;
        const rejectPrefix =
          ri?.by === 'partner'
            ? '✗ Vom Partner abgelehnt'
            : ri?.by === 'user'
              ? '✗ Von dir abgelehnt'
              : '✗ Abgelehnt';
        return (
          <Stack key={o.id} gap={4}>
            <ListingCard obj={o} />
            {o.status === 'rejected' && (
              <Group justify="space-between" wrap="wrap" gap="xs" px="xs">
                <Badge
                  color="red"
                  variant="light"
                  tt="none"
                  style={{ whiteSpace: 'normal', height: 'auto', lineHeight: 1.3, padding: '4px 8px' }}
                >
                  {rejectPrefix}
                  {ri ? ` · ${ri.label}` : ''}
                  {ri?.note ? ` — „${ri.note}"` : ''}
                </Badge>
                <Button
                  size="xs"
                  variant="light"
                  color="green"
                  onClick={() => reactivate.mutate(o.id)}
                  loading={reactivate.isPending && reactivate.variables === o.id}
                >
                  ↩ Reaktivieren
                </Button>
              </Group>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}
